import React, { useMemo, useState } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { brand } from "../data/estoreContent";
import { estoreShopUrl } from "../data/estorePaths";
import {
  marketingNavCategories,
  marketingCategoryProducts,
} from "../../marketingSchoolCatalog";

function renderSchoolSections(data, mainLabel, navigate) {
  return Object.entries(data).map(([subTitle, products]) => (
    <Box key={subTitle} sx={{ mb: 4 }}>
      <Box sx={{ fontSize: "18px", fontWeight: 800, mb: 2, position: "relative", color: brand.text }}>
        <Box
          sx={{
            width: 48,
            height: 4,
            background: `linear-gradient(90deg, ${brand.accent}, ${brand.accent2})`,
            borderRadius: 2,
            mb: 1,
          }}
        />
        {subTitle}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          scrollbarWidth: "thin",
        }}
      >
        {products.map((item, index) => (
          <Box
            key={index}
            onClick={() => navigate(estoreShopUrl(`${subTitle}`))}
            sx={{
              flex: "0 0 186px",
              minWidth: 186,
              background: "#fff",
              borderRadius: "12px",
              border: `1px solid ${brand.stroke}`,
              overflow: "hidden",
              cursor: "pointer",
              transition: "0.25s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 24px rgba(15,23,42,0.12)",
              },
            }}
          >
            <Box
              component="img"
              src={`${item.img}?w=400&h=300&fit=crop`}
              alt={subTitle}
              sx={{ width: "100%", height: 148, objectFit: "cover", bgcolor: "#F1F5F9" }}
            />
            <Box sx={{ p: 1.25 }}>
              <Typography variant="caption" sx={{ color: brand.muted, display: "block", mb: 0.25 }}>
                {mainLabel}
              </Typography>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>{subTitle}</Typography>
              <Typography sx={{ color: "#b12704", fontWeight: 800, fontSize: "14px" }}>{item.price}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  ));
}

/**
 * Same main categories + subcategory product rows as the Marketing page (below header).
 */
function EstoreSchoolProcurementHub() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const activeData = useMemo(() => {
    if (!selected) return null;
    return marketingCategoryProducts[selected] ?? null;
  }, [selected]);

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${brand.stroke}`,
        background: "linear-gradient(180deg, rgba(99,102,241,0.06) 0%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="lg" sx={{ py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflowX: "auto",
            whiteSpace: "nowrap",
            pb: 1,
            scrollbarWidth: "thin",
          }}
        >
          {marketingNavCategories.map((item) => {
            const isActive = selected === item;
            return (
              <Box
                key={item}
                onClick={() => setSelected(item)}
                sx={{
                  flex: "0 0 auto",
                  px: 2.25,
                  py: 1,
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 800,
                  cursor: "pointer",
                  color: isActive ? "#fff" : brand.accent,
                  background: isActive ? `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)` : "#fff",
                  border: isActive ? "none" : `1px solid rgba(99,102,241,0.35)`,
                  boxShadow: isActive ? "0 6px 16px rgba(99,102,241,0.28)" : "none",
                  transition: "all .2s ease",
                  "&:hover": {
                    background: isActive
                      ? `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`
                      : "rgba(99,102,241,0.08)",
                  },
                }}
              >
                {item}
              </Box>
            );
          })}
          {selected && (
            <Button
              size="small"
              onClick={() => setSelected(null)}
              sx={{ flex: "0 0 auto", textTransform: "none", fontWeight: 700, color: brand.muted }}
            >
              Clear
            </Button>
          )}
        </Box>

        {activeData && (
          <Box sx={{ pt: 2, pb: 1 }}>
            <Typography variant="body2" sx={{ color: brand.muted, mb: 2, maxWidth: 720 }}>
              Subcategories match the Marketing catalog. Tap a card to search the Estore shop, or open the full{" "}
              <Box
                component="span"
                onClick={() => navigate("/Marketing")}
                sx={{ color: brand.accent, fontWeight: 800, cursor: "pointer", textDecoration: "underline" }}
              >
                Marketing
              </Box>{" "}
              page.
            </Typography>
            {renderSchoolSections(activeData, selected, navigate)}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default EstoreSchoolProcurementHub;
